import React from 'react';
import { ArrowLeft, Camera, Palette, Eye, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template20 = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 font-sans">
            {/* Creative Header */}
            <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-orange-200">
                <div className="w-full px-4 lg:px-8 py-4 lg:py-5 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-orange-600 transition-colors"
                    >
                        <ArrowLeft size={18} />
                        <span className="text-sm">Back</span>
                    </button>
                    <div className="text-center">
                        <h1 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">STUDIO</h1>
                        <p className="text-xs lg:text-sm text-gray-500 tracking-widest">CREATIVE COLLECTIVE</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Camera className="text-orange-500" size={20} />
                        <Palette className="text-amber-500" size={20} />
                    </div>
                </div>
            </header>

            {/* Artist Hero */}
            <section className="relative py-16 lg:py-24 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-amber-400/20"></div>
                <div className="container mx-auto px-4 lg:px-8 relative z-10">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <div>
                                <h2 className="text-5xl lg:text-8xl font-bold bg-gradient-to-r from-amber-600 via-orange-500 to-yellow-600 bg-clip-text text-transparent mb-6 leading-tight">
                                    Creative
                                </h2>
                                <h3 className="text-3xl lg:text-5xl font-light text-gray-700 mb-8">Expression</h3>
                                <p className="text-lg lg:text-xl text-gray-600 leading-relaxed mb-8">
                                    Welcome to our creative studio where artistry meets fashion. Each piece tells a story,
                                    crafted with passion and designed to inspire.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <button className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-full hover:scale-105 transition-transform font-medium">
                                        View Gallery
                                    </button>
                                    <button className="border-2 border-orange-500 text-orange-600 px-8 py-4 rounded-full hover:bg-orange-500 hover:text-white transition-all font-medium">
                                        Artist Story
                                    </button>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-700">
                                    <img
                                        src="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=600&h=600&fit=crop"
                                        alt="Studio Creative"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full opacity-20"></div>
                                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full opacity-30"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio Gallery */}
            <section className="bg-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-6">
                            Portfolio
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto"></div>
                    </div>

                    {/* Masonry-style Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { height: 'aspect-[3/4]', image: `https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=800` },
                            { height: 'aspect-square', image: `https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&h=400&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=801` },
                            { height: 'aspect-[4/5]', image: `https://images.unsplash.com/photo-1560472355-536de3962603?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=802` },
                            { height: 'aspect-[3/4]', image: `https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=803` },
                            { height: 'aspect-square', image: `https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=400&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=804` },
                            { height: 'aspect-[3/4]', image: `https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=805` },
                            { height: 'aspect-[4/5]', image: `https://images.unsplash.com/photo-1560472355-536de3962603?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=806` },
                            { height: 'aspect-square', image: `https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400&h=400&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=807` },
                        ].map((item, index) => (
                            <div key={index} className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 shadow-lg hover:shadow-xl transition-all duration-500 ${item.height}`}>
                                <img
                                    src={item.image}
                                    alt={`Portfolio Item ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="flex justify-between items-end">
                                            <div>
                                                <h3 className="text-white font-semibold">Piece #{index + 1}</h3>
                                                <p className="text-orange-200 text-sm">Limited Edition</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Eye className="text-white cursor-pointer hover:text-orange-300" size={20} />
                                                <Heart className="text-white cursor-pointer hover:text-red-300" size={20} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Artist Statement */}
            <section className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <div className="max-w-4xl mx-auto">
                        <Palette className="mx-auto mb-8 text-white" size={64} />
                        <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
                            Art in Motion
                        </h2>
                        <p className="text-xl lg:text-2xl text-white/90 leading-relaxed mb-8">
                            "Every piece I create is a reflection of emotions, experiences, and dreams.
                            Fashion is my canvas, and each design tells a unique story of creativity and passion."
                        </p>
                        <div className="text-white/80 text-lg">
                            — Creative Director, Studio
                        </div>
                    </div>
                </div>
            </section>

            {/* Collections */}
            <section className="bg-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-6">
                            Collections
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Explore our curated collections, each representing a different facet of artistic expression
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {[
                            { name: "Abstract", theme: "Bold geometric patterns", items: 12 },
                            { name: "Nature", theme: "Organic flowing designs", items: 8 },
                            { name: "Urban", theme: "City-inspired aesthetics", items: 15 }
                        ].map((collection, index) => (
                            <div key={index} className="group text-center">
                                <div className="aspect-square rounded-3xl overflow-hidden mb-6 bg-gradient-to-br from-orange-100 to-amber-100 shadow-lg group-hover:shadow-2xl transition-all duration-500 transform group-hover:scale-105">
                                    <img
                                        src={`https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=400&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${900 + index}`}
                                        alt={collection.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{collection.name}</h3>
                                <p className="text-gray-600 mb-3">{collection.theme}</p>
                                <p className="text-sm text-orange-600 font-medium">{collection.items} pieces</p>
                                <button className="mt-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-full hover:scale-105 transition-transform">
                                    Explore Collection
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Studio */}
            <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-6">
                        Visit Our Studio
                    </h2>
                    <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                        Experience our creative process firsthand. Schedule a visit to see art in the making.
                    </p>
                    <div className="max-w-md mx-auto">
                        <button className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-4 rounded-full text-lg font-medium hover:scale-105 transition-transform">
                            Book Studio Visit
                        </button>
                    </div>
                </div>
            </section>

            {/* Creative Footer */}
            <footer className="bg-gray-900 text-white py-12 lg:py-16">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent mb-4">
                            STUDIO
                        </h3>
                        <p className="text-gray-400 max-w-lg mx-auto">
                            Where creativity meets craftsmanship. Follow our journey as we continue to push the boundaries of artistic fashion.
                        </p>
                    </div>
                    <div className="flex justify-center gap-8 text-sm text-gray-400 mb-8">
                        <span className="hover:text-orange-400 cursor-pointer transition-colors">Instagram</span>
                        <span className="hover:text-orange-400 cursor-pointer transition-colors">Portfolio</span>
                        <span className="hover:text-orange-400 cursor-pointer transition-colors">Contact</span>
                        <span className="hover:text-orange-400 cursor-pointer transition-colors">Blog</span>
                    </div>
                    <div className="text-center">
                        <p className="text-xs text-gray-500">&copy; 2025 Studio Creative. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template20;